// The footer every page carries (gabriel-mille/sensa-paved-road#461), from the Setalba Network
// design rules (DA "Pied de page", ODR-0129): the contact e-mail, the legal notice, the privacy
// notice, the project's own links, and the signature "Made with ♥ by Setalba Network".
//
// The LCEN asks for the legal notice and the contact to be reachable "easily, directly and
// permanently": a link in the footer of every page is that access, so they never move into the
// page body. The legal notice is the single EI page (AgDR-0277); the privacy notice is the app's.
//
// The signature is the brand's, never translated, the same on every product: the small heart
// drawn for the background (light or dark), then "Setalba Network" in the Newton gradient on a
// dark background and in black over the prism rule on a light one (`signature-name`, defined in
// the shared tailwind-config preset so `spr sync` keeps it identical everywhere).
//
// It sits at the bottom of the window because the shell is a flex column at least as tall as the
// window with a growing `main` (App.tsx); the footer itself is never `position: fixed`.
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

export interface FooterLink {
  /** The visible text, already translated by the caller. */
  label: string;
  href: string;
}

export interface SiteFooterProps {
  /** The project's contact address, shown and linked as `mailto:`. */
  contactEmail: string;
  /** The legal notice page (the EI's single page, AgDR-0277). */
  legalNoticeUrl: string;
  /** The app's privacy notice. */
  privacyUrl: string;
  /** The project's own links (help, status, a store page…), after the three above. */
  links?: readonly FooterLink[];
}

/** Where "Setalba Network" leads: the EI's site. */
const SIGNATURE_URL = 'https://u2661.net';

const LINK_CLASS = 'underline underline-offset-4 hover:text-slate-900 dark:hover:text-slate-100';

/** An app route goes through the router (no page reload); any other URL is a plain link. */
function FooterAnchor({ href, children }: Readonly<{ href: string; children: string }>) {
  return href.startsWith('/') && !href.startsWith('//') ? (
    <Link to={href} className={LINK_CLASS}>
      {children}
    </Link>
  ) : (
    <a href={href} className={LINK_CLASS}>
      {children}
    </a>
  );
}

/** The brand signature, never translated (DA "Pied de page"); its heart reads ♡ as text. */
function Signature() {
  return (
    <p lang="en">
      Made with{' '}
      <img
        src="/brand/heart-small-on-light.svg"
        alt="♡"
        width={14}
        height={14}
        className="inline-block align-[-2px] dark:hidden"
      />
      <img
        src="/brand/heart-small-on-dark.svg"
        alt="♡"
        width={14}
        height={14}
        className="hidden align-[-2px] dark:inline-block"
      />{' '}
      by{' '}
      <a href={SIGNATURE_URL} className="signature-name">
        Setalba Network
      </a>
    </p>
  );
}

export function SiteFooter({
  contactEmail,
  legalNoticeUrl,
  privacyUrl,
  links = [],
}: Readonly<SiteFooterProps>) {
  const { t } = useTranslation();
  return (
    // `data-site-footer` is the stable marker e2e/site-footer.spec.ts looks for: a project that
    // has not adopted this footer yet has no marker, and that spec skips instead of failing.
    <footer
      data-site-footer=""
      className="border-t border-slate-300 py-6 text-sm text-slate-700 dark:border-slate-800 dark:text-slate-300"
    >
      <div className="mx-auto flex max-w-2xl flex-col gap-4 px-4">
        <nav aria-label={t('footer.label')}>
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            <li>
              <a href={`mailto:${contactEmail}`} className={LINK_CLASS}>
                {contactEmail}
              </a>
            </li>
            <li>
              <FooterAnchor href={legalNoticeUrl}>{t('footer.legalNotice')}</FooterAnchor>
            </li>
            <li>
              <FooterAnchor href={privacyUrl}>{t('footer.privacy')}</FooterAnchor>
            </li>
            {links.map((link) => (
              <li key={link.href}>
                <FooterAnchor href={link.href}>{link.label}</FooterAnchor>
              </li>
            ))}
          </ul>
        </nav>
        <Signature />
      </div>
    </footer>
  );
}
